import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { Type } from '@angular/core';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { Route } from '@angular/router';

import { authenticatedDomain } from '@portal/shared/domain';
import {
    AdminAuthNavigatorService,
    ApplicationFeatures,
    AUTH_MANAGER,
    AUTH_NAME,
    AUTH_NAVIGATOR,
    DATE_FORMATS,
    securityRoleCanActivateGuard,
    SecurityRoles,
    tenantIdInterceptor,
    UserAuthNavigatorService,
    UserAuthService,
} from '@portal/shared/utils';

import {
    apiUrlInterceptor,
    authCanActivateGuard,
    caseFilesCanMatchGuard,
    directorOrManagerCanMatchGuard,
    getFeatureCanMatchGuard,
    locationsCanMatchGuard,
    nonProductionCanMatchGuard,
    notOfficeAdminCanMatchGuard,
    notPatientCanActivateGuard,
    onboardingCanActivateGuard,
    onboardingCanMatchGuard,
    TldUtils,
} from '@portal/core/utils';

import { PortalApiController } from '@portal/portal/data-access';
import { portalCalendarCanActivateGuard } from '@portal/portal/features';
import { portalAuthInterceptor, PortalAuthManager } from '@portal/portal/utils';
import { TenantNexusAuthNavigatorService, TenantNexusAuthService } from '@portal/tenant-nexus/utils';
import { userDomain } from '@portal/user/domain';

import { ForbiddenPageComponent } from './forbidden-page/forbidden-page.component';
import { NotFoundPageComponent } from './not-found-page/not-found-page.component';

const publicRoutes: Route[] = [
    {
        path: 'public',
        loadComponent: async () => (await import('@portal/shared/routes')).PublicLayoutComponent,
        children: [
            {
                path: 'report',
                loadChildren: async () => (await import('@portal/report/routes')).routes,
            },
        ],
    },
    {
        providers: [provideLuxonDateAdapter(DATE_FORMATS)],
        path: 'portal',
        loadChildren: async () => (await import('@portal/portal/routes')).routes,
    },
    {
        path: 'booking/:calendarPath',
        providers: [
            {
                provide: AUTH_MANAGER,
                useExisting: PortalAuthManager,
            },
            provideHttpClient(withXhr(), withInterceptors([apiUrlInterceptor, tenantIdInterceptor, portalAuthInterceptor])),
            PortalApiController,
        ],
        canActivate: [portalCalendarCanActivateGuard],
        children: [],
    },
];

const publicTenantRoutes: Route[] = [
    {
        path: '',
        providers: [
            {
                provide: AUTH_NAME,
                useValue: 'authUserToken',
            },
            {
                provide: AUTH_MANAGER,
                useExisting: UserAuthService,
            },
            {
                provide: AUTH_NAVIGATOR,
                useClass: UserAuthNavigatorService,
            },
            userDomain,
        ],
        loadComponent: async () => (await import('@portal/shared/routes')).PublicLayoutComponent,
        children: [
            {
                path: '',
                pathMatch: 'full',
                redirectTo: 'user',
            },
            {
                path: 'user',
                canMatch: [getFeatureCanMatchGuard(ApplicationFeatures.USER_APPLICATION)],
                loadChildren: async () => (await import('@portal/user/routes')).routes,
            },
            {
                path: 'recovery',
                canMatch: [getFeatureCanMatchGuard(ApplicationFeatures.RECOVERY_APPLICATION)],
                loadChildren: async () => (await import('@portal/recovery/routes')).routes,
            },
        ],
    },
] satisfies Route[];

const publicTenantAdminRoutes: Route[] = [
    {
        path: '',
        providers: [
            {
                provide: AUTH_NAME,
                useValue: 'authUserToken',
            },
            {
                provide: AUTH_MANAGER,
                useExisting: UserAuthService,
            },
            {
                provide: AUTH_NAVIGATOR,
                useClass: AdminAuthNavigatorService,
            },
            userDomain,
        ],
        loadComponent: async () => (await import('@portal/shared/routes')).PublicLayoutComponent,
        children: [
            {
                path: '',
                pathMatch: 'full',
                redirectTo: 'admin',
            },
            {
                path: 'admin',
                canMatch: [nonProductionCanMatchGuard],
                loadChildren: async () => (await import('@portal/tenant-admin/routes')).routes,
            },
        ],
    },
] satisfies Route[];

const authenticatedTenantRoutes: Route[] = [
    {
        path: '',
        providers: [
            {
                provide: AUTH_NAME,
                useValue: 'authUserToken',
            },
            {
                provide: AUTH_MANAGER,
                useExisting: UserAuthService,
            },
            {
                provide: AUTH_NAVIGATOR,
                useClass: UserAuthNavigatorService,
            },
            authenticatedDomain,
        ],
        canActivate: [authCanActivateGuard, notPatientCanActivateGuard],
        loadComponent: async () => (await import('@portal/shared/routes')).AuthenticatedLayoutComponent,
        children: [
            {
                path: '',
                pathMatch: 'full',
                redirectTo: 'dashboard',
            },
            {
                path: 'onboarding',
                canMatch: [getFeatureCanMatchGuard(ApplicationFeatures.ONBOARDING_APPLICATION), onboardingCanMatchGuard],
                loadChildren: async () => (await import('@portal/onboarding/routes')).routes,
            },
            {
                path: 'portals',
                canMatch: [getFeatureCanMatchGuard(ApplicationFeatures.PORTALS_APPLICATION), directorOrManagerCanMatchGuard],
                loadChildren: async () => (await import('@portal/portals/routes')).routes,
            },
            {
                path: '',
                canActivate: [onboardingCanActivateGuard],
                children: [
                    {
                        path: 'dashboard',
                        canMatch: [getFeatureCanMatchGuard(ApplicationFeatures.DASHBOARD_APPLICATION)],
                        loadChildren: async () => (await import('@portal/dashboard/routes')).routes,
                    },
                    {
                        path: 'contacts',
                        canMatch: [getFeatureCanMatchGuard(ApplicationFeatures.CONTACTS_APPLICATION), directorOrManagerCanMatchGuard],
                        loadChildren: async () => (await import('@portal/contacts/routes')).routes,
                    },
                    {
                        path: 'calendar',
                        canMatch: [getFeatureCanMatchGuard(ApplicationFeatures.CALENDAR_APPLICATION)],
                        loadChildren: async () => (await import('@portal/calendar/routes')).routes,
                    },
                    {
                        path: 'case-files',
                        canMatch: [getFeatureCanMatchGuard(ApplicationFeatures.CASE_FILE_APPLICATION), caseFilesCanMatchGuard],
                        loadChildren: async () => (await import('@portal/case-files/routes')).routes,
                    },
                    {
                        path: 'charting',
                        canMatch: [getFeatureCanMatchGuard(ApplicationFeatures.CHARTING_APPLICATION), notOfficeAdminCanMatchGuard],
                        loadChildren: async () => (await import('@portal/charting/routes')).routes,
                    },
                    {
                        path: 'accounting',
                        canMatch: [getFeatureCanMatchGuard(ApplicationFeatures.ACCOUNTING_APPLICATION), directorOrManagerCanMatchGuard],
                        loadChildren: async () => (await import('@portal/accounting/routes')).routes,
                    },
                    {
                        path: 'admin-console',
                        canActivate: [securityRoleCanActivateGuard(SecurityRoles.MANAGER)],
                        canMatch: [getFeatureCanMatchGuard(ApplicationFeatures.ADMIN_CONSOLE_APPLICATION), locationsCanMatchGuard],
                        loadChildren: async () => (await import('@portal/admin-console/routes')).routes,
                    },
                    {
                        path: 'reports',
                        canMatch: [getFeatureCanMatchGuard(ApplicationFeatures.REPORTS_APPLICATION), directorOrManagerCanMatchGuard],
                        loadChildren: async () => (await import('@portal/reports/routes')).routes,
                    },
                    {
                        path: 'team',
                        canMatch: [getFeatureCanMatchGuard(ApplicationFeatures.TEAM_APPLICATION), directorOrManagerCanMatchGuard],
                        loadChildren: async () => (await import('@portal/team/routes')).routes,
                    },
                    {
                        path: 'forms',
                        canMatch: [getFeatureCanMatchGuard(ApplicationFeatures.FORMS_APPLICATION)],
                        loadChildren: async () => (await import('@portal/forms/routes')).routes,
                    },
                ],
            },
        ],
    },
] satisfies Route[];

const tenantNexusRoutes: Route[] = [
    {
        path: '',
        canMatch: [nonProductionCanMatchGuard],
        providers: [
            {
                provide: AUTH_NAME,
                useValue: 'authTenantNexusToken',
            },
            {
                provide: AUTH_MANAGER,
                useExisting: TenantNexusAuthService,
            },
            {
                provide: AUTH_NAVIGATOR,
                useClass: TenantNexusAuthNavigatorService,
            },
        ],
        children: [
            {
                path: '',
                pathMatch: 'full',
                redirectTo: 'tenant-nexus',
            },
            {
                path: 'tenant-nexus',
                loadChildren: async () => (await import('@portal/tenant-nexus/routes')).routes,
            },
        ],
    },
] satisfies Route[];

const devRoute: Route = {
    path: 'dev',
    canMatch: [nonProductionCanMatchGuard],
    loadComponent: async () => (await import('@portal/shared/routes')).SandboxComponent as Type<unknown>,
};

export function getAppRoutes(isDevMode: boolean): Route[] {
    return [
        ...(isDevMode ? [devRoute] : []),
        ...(isDevMode && TldUtils.hasDomain() && !TldUtils.hasSubdomain() ? tenantNexusRoutes : []),
        ...(TldUtils.hasSubdomain()
            ? [...authenticatedTenantRoutes, ...publicRoutes, ...publicTenantRoutes, ...publicTenantAdminRoutes]
            : []),
        {
            path: '403',
            component: ForbiddenPageComponent,
        },
        {
            path: '404',
            component: NotFoundPageComponent,
        },
        {
            path: '**',
            redirectTo: '404',
        },
    ] satisfies Route[];
}
